/**
 * useResetApplyOnConfigEdit — keep the live preview live after an Apply.
 *
 * After Apply, the drawer surfaces the applied snapshot (`applyMutation.data`)
 * so the user sees the deltas just pushed. But that snapshot must NOT outlive
 * the next edit: the moment the user changes an exclusion rule, the formula, or
 * the weight bounds, the applied result is stale and the panel should fall back
 * to the live preview. Without this, the preview froze on the applied state and
 * only refreshed when the drawer was closed and reopened.
 *
 * We reset the mutation (clearing its `data`) on the FIRST edit after an apply,
 * so the existing `applyMutation.data ?? live.data` precedence naturally yields
 * the live result again. Standard-tab edits / the post-apply reseed touch the
 * flow draft, not these fields, so they never trip this reset.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { useEffect, useMemo, useRef } from 'react';
import type { FlowOptimizerConfig } from '@trafficpulse/shared';

interface ResettableMutation {
  data: unknown;
  reset: () => void;
}

export function useResetApplyOnConfigEdit(
  draft: FlowOptimizerConfig,
  applyMutation: ResettableMutation,
): void {
  const editableKey = useMemo(
    () =>
      JSON.stringify([
        draft.mode,
        draft.excludeRules,
        draft.formula,
        draft.minWeight,
        draft.maxWeight,
      ]),
    [draft.mode, draft.excludeRules, draft.formula, draft.minWeight, draft.maxWeight],
  );
  const mutationRef = useRef(applyMutation);
  mutationRef.current = applyMutation;
  const lastKeyRef = useRef(editableKey);

  useEffect(() => {
    if (lastKeyRef.current === editableKey) return;
    lastKeyRef.current = editableKey;
    if (mutationRef.current.data != null) mutationRef.current.reset();
  }, [editableKey]);
}
